/* eslint-disable react/forbid-elements, react/no-array-index-key -- TODO [CU-86c99qj3v]: refactor fat route into feature components */
import * as React from 'react';
import { createFileRoute, useParams, useRouteContext, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { type MediaUnitDetail, type CostHistoryEntry, isNotFoundError } from '@ooh/contracts';
import { mediaUnits } from '@ooh/contracts';
import { MediaEditForm } from '../../../../features/media-units/components/media-edit-form';
import { mediaUnitDetailQuery, mediaUnitKeys } from '../../../../features/media-units/api/queries';
import { MediaUnitNotFoundError } from '../../../../features/media-units/components/media-unit-not-found';
import { ErrorFallback } from '../../../../components/error-fallback';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { PageTitleContext } from '../../../__root';

function MediaEditErrorComponent({ error, reset }: ErrorComponentProps) {
  // Safe within errorComponent (unlike useLoaderData) — forwards the catalog
  // filters so "back to catalog" still restores them when the unit itself
  // 404s while editing. CU-86cb1nq9n.
  const { catalogSearch } = useSearch({ from: '/media-units/$id/edit/' });
  if (isNotFoundError(error)) {
    return <MediaUnitNotFoundError catalogSearch={catalogSearch} />;
  }
  return <ErrorFallback error={error} reset={reset} />;
}

/**
 * `catalogSearch` carries the catalog's opaque, serialized filter/sort state
 * forward from the detail page so the "back to catalog" link (reached via
 * this edit page's own "back to detail" link) can restore it. See
 * `lib/catalog-return-context.ts`. CU-86cajbuug.
 */
const MediaEditSearchSchema = z
  .object({
    catalogSearch: z.string().optional(),
  })
  .strict();

export const Route = createFileRoute('/media-units/$id/edit/')({
  validateSearch: MediaEditSearchSchema,
  component: MediaEditFormPage,
  errorComponent: MediaEditErrorComponent,
});

function DetailSkeleton() {
  return (
    <div className="p-6 max-w-4xl" data-testid="skeleton-loading">
      <div className="h-5 w-32 rounded bg-muted animate-pulse mb-5" />
      <div className="h-8 w-48 rounded bg-muted animate-pulse mb-6" />
      <div className="space-y-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-48 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    </div>
  );
}

export function MediaEditFormPage() {
  const { id } = useParams({ from: '/media-units/$id/edit/' });
  const { catalogSearch } = useSearch({ from: '/media-units/$id/edit/' });
  const { auth } = useRouteContext({ from: '__root__' });
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t } = useTranslation('inventory');

  const { data: unitData, isLoading, isError, error } = useQuery(mediaUnitDetailQuery(auth, id));
  const { data: costHistoryData } = useQuery({
    queryKey: mediaUnitKeys.costHistory(id),
    queryFn: ({ signal }) =>
      auth.callWithAuth(mediaUnits.costHistory, { id: Number(id) }, { signal }),
    enabled: !!unitData,
  });

  React.useEffect(() => {
    const unitForTitle = (unitData as { data: MediaUnitDetail } | undefined)?.data;
    setPageTitle(
      unitForTitle
        ? t('form.edit.pageTitle', { displayNumber: unitForTitle.displayNumber })
        : t('form.edit.title'),
    );
  }, [setPageTitle, t, unitData]);

  if (isLoading) return <DetailSkeleton />;
  if (isError) throw error;
  if (!unitData) return null;

  const unit = (unitData as { data: MediaUnitDetail }).data;
  const costHistory: CostHistoryEntry[] =
    (costHistoryData as { data?: CostHistoryEntry[] })?.data ?? [];
  return (
    <MediaEditForm unit={unit} costHistory={costHistory} id={id} catalogSearch={catalogSearch} />
  );
}
